import React, { useEffect, useMemo, useState } from "react";
import {
    Drawer,
    Stack,
    Group,
    Button,
    Textarea,
    TextInput,
    Text,
    Divider,
    Select,
    Alert,
} from "@mantine/core";
import { DateInput, TimeInput } from "@mantine/dates";
import { showNotification } from "@mantine/notifications";
import { IconDeviceFloppy, IconX } from "@tabler/icons-react";

import {
    PublicTransportBookingReadDto,
    UpdateMyTransportBookingDto,
} from "../../types/transportPublicTypes";
import { updateMyTransportBooking } from "../../api/transportPublicApi";
import { useTransportLocations } from "./useTransportLocations";

const RED = "#ff5e5e";

type Props = {
    opened: boolean;
    onClose: () => void;
    booking: PublicTransportBookingReadDto | null;
    eventId: number;
    userId: number;
    onSaved: () => void;
};

function isArrival(kind: unknown) {
    return kind === 1 || String(kind).toLowerCase() === "arrival";
}

function isDeparture(kind: unknown) {
    return kind === 2 || String(kind).toLowerCase() === "departure";
}

export default function EditMyBookingDrawer(props: Props) {
    const {
        opened,
        onClose,
        booking,
        eventId,
        userId,
        onSaved,
    } = props;

    const [scheduledDate, setScheduledDate] =
        useState<Date | null>(null);

    const [scheduledTime, setScheduledTime] = useState("");
    const [flightNo, setFlightNo] = useState("");
    const [pickUpPlace, setPickUpPlace] = useState("");
    const [dropOffPlace, setDropOffPlace] = useState("");
    const [contactPerson, setContactPerson] = useState("");
    const [numberOfPersons, setNumberOfPersons] = useState("");
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);

    const {
        loading: loadingLoc,
        error: locError,
        pickupSuggestions,
        dropoffSuggestions,
    } = useTransportLocations(String(eventId), "dk");

    const pickupOptions = useMemo(
        () =>
            pickupSuggestions.map((name) => ({
                value: name,
                label: name,
            })),
        [pickupSuggestions]
    );

    const dropoffOptions = useMemo(
        () =>
            dropoffSuggestions.map((name) => ({
                value: name,
                label: name,
            })),
        [dropoffSuggestions]
    );

    useEffect(() => {
        if (!opened || !booking) {
            return;
        }

        const dateValue =
            (booking.scheduledDate ?? "").slice(0, 10);

        setScheduledDate(
            dateValue
                ? new Date(`${dateValue}T12:00:00`)
                : null
        );

        const timeValue =
            (booking.scheduledTime ?? "").trim();

        setScheduledTime(
            timeValue ? timeValue.slice(0, 5) : ""
        );

        setFlightNo(booking.flightNo ?? "");
        setPickUpPlace(booking.pickUpPlace ?? "");
        setDropOffPlace(booking.dropOffPlace ?? "");
        setContactPerson(booking.contactPerson ?? "");

        setNumberOfPersons(
            booking.numberOfPersons != null
                ? String(booking.numberOfPersons)
                : ""
        );

        setNote(booking.note ?? "");
    }, [opened, booking]);

    const save = async () => {
        if (!booking) {
            return;
        }

        if (
            Number.isNaN(userId) ||
            Number.isNaN(eventId)
        ) {
            showNotification({
                color: "red",
                title: "Ugyldigt link",
                message:
                    "UserId eller eventId mangler i linket.",
            });

            return;
        }

        const numberOfPersonsValue =
            numberOfPersons.trim()
                ? Number(numberOfPersons)
                : null;

        if (
            numberOfPersonsValue !== null &&
            (
                Number.isNaN(numberOfPersonsValue) ||
                numberOfPersonsValue < 1
            )
        ) {
            showNotification({
                color: "red",
                title: "Ugyldigt antal",
                message:
                    "Antal personer skal være mindst 1.",
            });

            return;
        }

        const dto: UpdateMyTransportBookingDto = {
            scheduledDate: scheduledDate
                ? scheduledDate.toISOString().slice(0, 10)
                : null,

            scheduledTime: scheduledTime
                ? `${scheduledTime}:00`
                : null,

            flightNo: flightNo.trim()
                ? flightNo.trim()
                : null,

            contactPerson: contactPerson.trim()
                ? contactPerson.trim()
                : null,

            numberOfPersons: numberOfPersons
                ? Number(numberOfPersons)
                : null,

            note: note.trim()
                ? note.trim()
                : null,

            pickUpPlace: isDeparture(booking.kind)
                ? pickUpPlace.trim() || null
                : null,

            dropOffPlace: isArrival(booking.kind)
                ? dropOffPlace.trim() || null
                : null,
        };

        setSaving(true);

        try {
            await updateMyTransportBooking(
                eventId,
                userId,
                booking.transportRequestId,
                dto
            );

            showNotification({
                color: "green",
                title: "Gemt",
                message: "Booking opdateret.",
            });

            await onSaved();
            onClose();
        } catch (error: any) {
            showNotification({
                color: "red",
                title: "Fejl",
                message:
                    error?.response?.data?.message ||
                    error?.response?.data ||
                    error?.message ||
                    "Kunne ikke gemme booking.",
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Drawer
            opened={opened}
            onClose={onClose}
            position="right"
            size="md"
            title="Rediger booking"
            overlayProps={{ opacity: 0.25 }}
        >
            <Stack gap="md">
                <Text c="dimmed" size="sm">
                    Ret dine bookingoplysninger.
                </Text>

                <Divider />

                <DateInput
                    label="Scheduled date"
                    placeholder="Vælg dato..."
                    value={scheduledDate}
                    onChange={setScheduledDate}
                />

                <TimeInput
                    label="Scheduled time"
                    value={scheduledTime}
                    onChange={(event) =>
                        setScheduledTime(
                            event.currentTarget.value
                        )
                    }
                    withSeconds={false}
                />

                <TextInput
                    label="Flight number"
                    value={flightNo}
                    onChange={(event) =>
                        setFlightNo(
                            event.currentTarget.value
                        )
                    }
                />

                {locError && (
                    <Alert
                        color="yellow"
                        title="Lokationer"
                    >
                        {locError}
                    </Alert>
                )}

                {isDeparture(booking?.kind) && (
                    <Select
                        label="Pick-up place"
                        placeholder={
                            loadingLoc
                                ? "Henter lokationer..."
                                : "Vælg pick-up sted"
                        }
                        data={pickupOptions}
                        value={pickUpPlace || null}
                        onChange={(value) =>
                            setPickUpPlace(value ?? "")
                        }
                        searchable
                        nothingFoundMessage="Ingen steder fundet"
                        disabled={loadingLoc}
                    />
                )}

                {isArrival(booking?.kind) && (
                    <Select
                        label="Drop-off place"
                        placeholder={
                            loadingLoc
                                ? "Henter lokationer..."
                                : "Vælg drop-off sted"
                        }
                        data={dropoffOptions}
                        value={dropOffPlace || null}
                        onChange={(value) =>
                            setDropOffPlace(value ?? "")
                        }
                        searchable
                        nothingFoundMessage="Ingen steder fundet"
                        disabled={loadingLoc}
                    />
                )}

                <TextInput
                    label="Contact person"
                    value={contactPerson}
                    onChange={(event) =>
                        setContactPerson(
                            event.currentTarget.value
                        )
                    }
                />

                <TextInput
                    label="Number of persons"
                    value={numberOfPersons}
                    onChange={(event) =>
                        setNumberOfPersons(
                            event.currentTarget.value.replace(
                                /\D/g,
                                ""
                            )
                        )
                    }
                />

                <Textarea
                    label="Note"
                    placeholder="Evt. note..."
                    value={note}
                    onChange={(event) =>
                        setNote(
                            event.currentTarget.value
                        )
                    }
                    autosize
                    minRows={3}
                />

                <Group
                    justify="space-between"
                    mt="sm"
                >
                    <Button
                        variant="default"
                        leftSection={<IconX size={16} />}
                        onClick={onClose}
                        disabled={saving}
                    >
                        Luk
                    </Button>

                    <Button
                        leftSection={
                            <IconDeviceFloppy size={16} />
                        }
                        onClick={save}
                        loading={saving}
                        style={{
                            backgroundColor: RED,
                        }}
                    >
                        Gem
                    </Button>
                </Group>
            </Stack>
        </Drawer>
    );
}